import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@invento/ui/components/button';
import { Card, CardBody } from '@invento/ui/components/card';
import { AlertTriangle } from 'lucide-react';
import * as Sentry from '@sentry/react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Phase 6 — capture render-tree crashes in Sentry. This is the
    // canonical "unexpected exception" path for the frontend, per
    // FRONTEND_OBSERVABILITY_SECURITY.md. Safe context only:
    // route + a hash of the component-stack length, never user PII
    // or auth state.
    Sentry.captureException(error, {
      tags: {
        boundary: 'AppErrorBoundary',
        route: window.location?.pathname ?? 'unknown',
      },
      extra: {
        componentStackLength: errorInfo.componentStack?.length ?? 0,
      },
    });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card variant="outlined" className="max-w-md w-full">
          <CardBody className="flex flex-col items-center gap-4 text-center">
            <AlertTriangle className="h-12 w-12 text-red-500" />

            <h2 className="text-xl font-semibold text-gray-900">
              Something went wrong
            </h2>

            <p className="text-sm text-gray-600">
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>

            <Button variant="primary" onClick={this.handleReload}>
              Try Again
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }
}
